import React from 'react';
import { Star, Quote, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocalizedData } from '../i18n/dataLoader';

const StarRow = ({ rating, className = '' }) => (
  <div className={`flex gap-0.5 ${className}`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        className={`w-3.5 h-3.5 ${i <= rating ? 'text-[#b8893d] fill-[#b8893d]' : 'text-gray-300'}`}
      />
    ))}
  </div>
);

/**
 * Testimonials section.
 * @param {Object} props
 * @param {string} [props.rechtsgebiet] — wenn gesetzt, werden nur passende Stimmen gezeigt
 * @param {string} [props.title]
 * @param {string} [props.bg] — background variant: 'light' | 'cream'
 */
const Testimonials = ({ rechtsgebiet, title, bg = 'light' }) => {
  const { t } = useTranslation();
  const data = useLocalizedData('testimonials');
  const testimonials = data?.testimonials || [];
  const overallRating = data?.overallRating || { average: 4.9, count: '', source: '' };
  const items = rechtsgebiet
    ? testimonials.filter((t) => t.rechtsgebiet === rechtsgebiet)
    : testimonials;
  if (items.length === 0) return null;

  const displayed = items.slice(0, rechtsgebiet ? 3 : 6);
  const bgClass = bg === 'cream' ? 'bg-[#fbf7f0]' : 'bg-[#f5f8fa]';

  return (
    <section className={`py-20 ${bgClass}`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-14">
          <p className="text-[#b8893d] text-xs tracking-[0.3em] mb-3">{t('testimonials.eyebrow', 'MANDANTEN-STIMMEN')}</p>
          <h2 className="font-serif text-3xl md:text-4xl text-[#1B3A5C] mb-4">
            {title || t('testimonials.headline', 'Was unsere Mandanten sagen')}
          </h2>
          <div className="inline-flex items-center gap-3 mt-2">
            <StarRow rating={5} />
            <span className="text-[#1B3A5C] font-semibold">{overallRating.average} / 5</span>
            <span className="text-[#34536b] text-sm">({[overallRating.count, overallRating.source].filter(Boolean).join(' ')})</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayed.map((t) => (
            <div
              key={t.id}
              className="group relative bg-white p-7 rounded-sm border-l-4 border-[#b8893d] hover:shadow-xl transition-all duration-300"
            >
              <Quote className="absolute top-4 right-4 w-10 h-10 text-[#b8893d]/10" />
              <StarRow rating={t.rating} className="mb-4" />
              <p className="text-[#34536b] text-sm leading-relaxed mb-5 italic">„{t.quote}“</p>
              <div className="flex items-start gap-3 pt-4 border-t border-gray-100">
                <div className="w-10 h-10 rounded-full bg-[#1B3A5C] flex items-center justify-center text-white font-serif text-sm flex-shrink-0">
                  {t.name.split(' ').map((p) => p[0]).join('')}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[#1B3A5C] font-semibold text-sm">{t.name}</p>
                  <p className="text-[#34536b] text-xs">{t.role}</p>
                  <div className="flex items-center gap-1 text-[#34536b]/70 text-xs mt-1">
                    <MapPin className="w-3 h-3" /> {t.location}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
